import React, { useEffect, useMemo, useRef } from 'react';
import {
  useNavigate,
  useSearchParams,
  NavLink,
  useLocation,
} from 'react-router-dom';
import { useDebounceValue } from 'usehooks-ts';
import { IoMdAdd } from 'react-icons/io';
import Modal from '~/lib/Modal';
import { useGetEstimates, useGetEstimatesList, useGetEstimatesValidations } from '~/hooks/estimates';
import styles from './estimates.module.scss';
import FilterSearchParam from '~/components/FilterSearchParam';
import InputSearch from '~/components/InputSearch';
import CreateEstimate from '~/components/estimate/form/CreateEstimate';
import EstimatesTable from '~/components/table/EstimatesTable';
import { useUserProfile } from '~/hooks/auth';
import Pagination from '~/components/Pagination';
import Etaiements from '../etaiements';

const EstimatesScaffoldingTab = () => {
  const modalRef = useRef<any>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { search = '', ...paramsObject } = Object.fromEntries(searchParams);
  const [debouncedSearch] = useDebounceValue(search, 500);
  const { data: userAuth } = useUserProfile();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const hasPagination = params.has('limit') || params.has('page') || params.has('skip');
    if (!hasPagination) {
      params.set('page', '1');
      params.set('limit', '20');
      setSearchParams(params, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const {
    data: estimatesData,
  } = useGetEstimates({
    ...paramsObject,
    search: debouncedSearch,
    limit: paramsObject.limit || '20',
    page: paramsObject.page || '1',
  });

  const {
    data: validationsData,
  } = useGetEstimatesValidations({
    ...paramsObject,
  });
  const estimatesList = estimatesData?.estimates || [];
  const validationsList = validationsData?.estimates || [];

  const { data: list = {} } = useGetEstimatesList();
  const {
    agencies = [],
    status = [],
    departments = [],
    commercialUsers = [],
  } = list;

  const handleSearch = (searchValue: string) => {
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    if (searchValue.length < 3) {
      params.delete('search');
    } else {
      params.set('search', searchValue);
    }
    setSearchParams(params);
  };

  const validations = useMemo(() => validationsList.filter(
    (v) => v.author !== userAuth?._id,
  ), [validationsList, userAuth]);

  const estimates = useMemo(() => {
    if (validations.length === 0) {
      return estimatesList;
    }
    return estimatesList.filter(
      (e) => validations.length && validations.find((v) => v._id !== e._id),
    );
  }, [estimatesList, validations]);

  const handleSorting = (sort: any) => {
    if (!sort) return;
    const params = new URLSearchParams(searchParams);
    params.delete('skip');
    params.delete('page');
    params.set('sortKey', sort.id.replace('_', '.'));
    params.set('order', sort.desc ? 'desc' : 'asc');
    setSearchParams(new URLSearchParams(params));
  };

  return (
    <>
      <Modal width={450} ref={modalRef}>
        <CreateEstimate onSuccess={(estimateId: string) => {
          modalRef.current.close();
          navigate(`/estimates/${estimateId}`);
        }} />
      </Modal>
      <div className={styles.filters}>
        <div className={styles.selects}>
          <div className={styles.field}>
            <FilterSearchParam
              label='Agence'
              name='agency'
              options={agencies}
              feminine
            />
          </div>
          <div className={styles.field}>
            <FilterSearchParam
              label='Département'
              name='department'
              options={departments}
            />
          </div>
          <div className={styles.field}>
            <FilterSearchParam
              label='Statut'
              name='status'
              options={status}
            />
          </div>
          <div className={styles.field}>
            <FilterSearchParam
              label='Commercial'
              name='commercialUser'
              options={commercialUsers}
            />
          </div>
        </div>
        <div className={styles.search}>
          <InputSearch
            defaultValue={search}
            handleSearch={handleSearch}
            label='Rechercher'
          />
        </div>
        <button onClick={() => modalRef.current.open()}>
          <span className='icon'>{IoMdAdd({})}</span>
          <span>Créer un devis</span>
        </button>
      </div>
      {validations.length > 0
        && <>
          <div className={styles.validations}>
          <h4>Devis à valider</h4>
          <EstimatesTable
            estimates={validations}
            handleSorting={handleSorting}
          />
        </div>
        <h4>Liste des devis</h4>
        </>
      }
      <div className={styles.table}>
        {(estimates || [])?.length > 0 && (
          <EstimatesTable
            estimates={estimates}
            handleSorting={handleSorting}
          />
        )}
      </div>
      <div className={styles.pagination}>
        {estimatesData?.meta?.total && estimatesData?.meta?.limit
        && estimatesData.meta.total > estimatesData.meta.limit
        && estimatesData?.meta?.page
        && <Pagination
          currentPage={estimatesData.meta.page}
          totalItems={estimatesData.meta.total}
          itemsPerPage={estimatesData.meta.limit}
        />
        }
      </div>
    </>
  );
};

const Estimates = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: userAuth } = useUserProfile();

  const isScaffoldingActive = location.pathname === '/estimates';
  const isEtaiementActive = location.pathname === '/estimates/etaiements';

  const estimateAccessMode = userAuth?.estimateAccessMode || 'scaffolding';
  const isAdmin = userAuth?.role === 'Admin';
  const isSale = userAuth?.role === 'Sale';

  const canSeeScaffolding = isAdmin || !isSale || estimateAccessMode === 'scaffolding' || estimateAccessMode === 'both';
  const canSeeEtaiement = isAdmin || !isSale || estimateAccessMode === 'etaiement' || estimateAccessMode === 'both';

  useEffect(() => {
    if (isEtaiementActive && !canSeeEtaiement && canSeeScaffolding) {
      navigate('/estimates', { replace: true });
      return;
    }
    if (isScaffoldingActive && !canSeeScaffolding && canSeeEtaiement) {
      navigate('/estimates/etaiements', { replace: true });
    }
  }, [
    canSeeEtaiement,
    canSeeScaffolding,
    isEtaiementActive,
    isScaffoldingActive,
    navigate,
  ]);

  return (
    <div className={styles.estimates}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1>Les devis</h1>
        </div>
        <nav className={styles.tabs}>
          <ul>
            {canSeeScaffolding && (
              <li>
                <NavLink
                  className={() => (isScaffoldingActive ? styles.active : '')}
                  to="/estimates"
                >
                  Échafaudage
                </NavLink>
              </li>
            )}
            {canSeeEtaiement && (
              <li>
                <NavLink
                  className={() => (isEtaiementActive ? styles.active : '')}
                  to="/estimates/etaiements"
                >
                  Étaiement
                </NavLink>
              </li>
            )}
          </ul>
        </nav>
        {isEtaiementActive && canSeeEtaiement ? <Etaiements /> : <EstimatesScaffoldingTab />}
      </div>
    </div>
  );
};

export default Estimates;
